import { ChangeDetectorRef, Component, inject, Input, OnInit, Pipe, PipeTransform, QueryList, ViewChildren } from "@angular/core";
import { GetDefaultExplorerConfig, IModalConfig, IModalExplorerConfig, IModalFooterButton, ModalDialogs, ModalDialogSizes } from "../modal";
import { OptimaDefaultModalService } from "../modal.service";
import { BizEntityFunctionTypes } from "@optima-stride-ai/core";
import { ButtonModule, ModalModule } from "@coreui/angular";
import { Explorer } from "@optima-stride-ai/optima-components";
import { ConfirmationDialogueComponent } from "../../delete-confirmation-dialogue 2/confirmation-dialogue.component";
import { RenameDialogueComponent } from "../../rename-dialogue/rename-dialogue.component";
import { LibrariesPageComponent } from "../../../pages/libraries/libraries-page-refactored.component";
import { TemplateExplorerModalWrapperComponent } from '../../../pages/libraries/components/template-explorer-modal-wrapper/template-explorer-modal-wrapper.component';
import { AppFrameworkContextService, GenericExplorerComponent } from "@optima-stride-ai/optima-app-framework";


@Pipe({
  name: 'levelClass',
  standalone: true
})
export class DefaultModalLevelClassPipe implements PipeTransform {
  transform(lvl: number): string {
    return "level-" + lvl + " leveled-bg";
  }
}


@Pipe({
  name: 'hasFooter',
  standalone: true
})
export class DefaultModalHasFooterPipe implements PipeTransform {
  transform(c: IModalConfig): boolean {
    return c.footerButtons.length > 0;
  }
}

@Pipe({
  name: 'size',
  standalone: true
})
export class DefaultModalSizePipe implements PipeTransform {
  transform(c: IModalConfig): "sm" | "lg" | "xl" | undefined {
    switch (c.size) {
      case ModalDialogSizes.SM:
        return "sm";
      case ModalDialogSizes.LG:
        return "lg";
      case ModalDialogSizes.XL:
        return "xl";
      default:
        return undefined
    }
  }
}


//Implementation Notes: // This should be a single instance throughout the application.
// Avoid opening cascading modal dialogs. Meaning don't open one modal from inside another modal.
// UPDATE: 2024-12-02. Introducing cascading modals by adding levels.
//  Strategy:
//            1. For now add 3 levels
//            2. Each level will have a unique id.
//            3. The id will be used to identify the level.
//            4. Client code will not know the level, it will just call to open the dialog
//            5. The service will manage the levels. 

@Component({
    selector: 'app-default-modal',
    imports: [
    DefaultModalSizePipe,
    DefaultModalHasFooterPipe,
    DefaultModalLevelClassPipe,
    ModalModule,
    ButtonModule,
    ConfirmationDialogueComponent,
    RenameDialogueComponent,
  TemplateExplorerModalWrapperComponent,
    GenericExplorerComponent
],
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.sass'
})

export class DefaultModalComponent implements OnInit {

  @ViewChildren('targetComponent') targetComponents!: QueryList<any>;
  @Input() level: number = 1;

  modalService: OptimaDefaultModalService = inject(OptimaDefaultModalService);
  visible: boolean = false;
  c: IModalConfig | IModalExplorerConfig = GetDefaultExplorerConfig();
  changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  modalDialogs = ModalDialogs;
  iamVisible: boolean = false; //Since dialog is always there during app is up, we want to conditionally render body. Directly using visible property, abruptly removes the content of the dialog and then animates out.
  selectedBizEntityText: string = "Selected Biz Entities: ";
  private appFrameworkContextService = inject(AppFrameworkContextService);
  constructor() {
  }

  closeMe() {
    this.modalService.onDialogClose(this.level);
    setTimeout(() => { this.iamVisible = false; }, 250) //We want the animation to first run and then the set the variable which is being used to block the body to render.
    this.visible = false;
    this.cleanUp()
  }

  ngOnInit() {
    // console.log("I am here")
    this.modalService.register(this, this.level);
  }

  show(config: IModalConfig | IModalExplorerConfig) {
    this.iamVisible = true;
    this.c = { ...config };
    this.changeDetectorRef.detectChanges();
    this.visible = true;
  }

  onClick(config: IModalFooterButton) {
    let close = true;
    if (config.formValidate) {
      close = this.validateForm()
    };
    let data: any = undefined;
    if (config.getData) {
      data = this.getData();
    }
    if (close) {
      if (config.closesModal) this.closeMe()
      config?.click(data)
    }
  }

  isDisabled(config: IModalFooterButton): boolean {
    let disabled = false;

    // if (config.formValidate) {
    //   disabled = !this.validateForm()
    //   if(disabled) return disabled;
    // };
    let data: any = undefined;
    if (config.getData) {
      data = this.getData();
      if (data) {
        if (config.isEnabled) {
          disabled = !config.isEnabled(data);
        }
      }
      else {
        disabled = true;
      }
    }
    return disabled;
  }

  getData(): any {
    let component = this.getTargetComponentClass(this.c.dialog) as any;
    try {
      if (component) return component.data;
      else {
        return false
      };
    } catch (error) {
      return false
    }
  }

  validateForm(): boolean {
    //This will fail if the target component doesn't have a validate method.
    //In production code there should be type checking to ensure the target component has the validate method.
    let component = this.getTargetComponentClass(this.c.dialog) as any;
    try {
      if (component) return component.validate();
      else {
        return false
      };
    } catch (error) {
      return false
    }
  }

  private cleanUp() {
    //Following code was commented to fix an issue of ghost default dialog showing up when dialog is closed.
    //What I remember was that this method was a workaround to avoid multiple buttons getting added to the footer.
    //If the issue of multiple buttons arise again, re-think and re-design the logic.
    //@SanjayAtOptimaStride 2024-09-24

    //this.c = GetDefaultConfig();
    // document.getElementById("modalFooter")!.innerHTML = "";
    // this.changeDetectorRef.detectChanges();
  }

  footerButtonsTrackBy(index: number, button: IModalFooterButton) {
    return button.id;
  }

  getTargetComponentClass(dialog: ModalDialogs) {
    switch (dialog) {
      case ModalDialogs.ConfirmationDialogue:
        return this.targetComponents.first as ConfirmationDialogueComponent;
      case ModalDialogs.RenameDialogue:
        return this.targetComponents.first as RenameDialogueComponent;
      case ModalDialogs.LibraryChooser:
        return this.targetComponents.first as TemplateExplorerModalWrapperComponent;
      case ModalDialogs.GenericExplorer:
        return this.targetComponents.first as GenericExplorerComponent;
      default:
        return undefined;
    }
  }

  get functionTypeFilter(): BizEntityFunctionTypes | null {
    if (this.c.hasOwnProperty('bizEntityFunctionTypeFilter')) {
      return (this.c as IModalExplorerConfig).bizEntityFunctionTypeFilter;
    }
    return null;
  }

  get explorerEntityTypeIds(): string[] {
    return (this.c as IModalExplorerConfig).entityTypeIds ?? [];
  }

  get explorerAppId(): string {
    let appId : string | undefined;
    const currentApp = this.appFrameworkContextService.currentApp;
    if(currentApp?.appId !== (this.c as IModalExplorerConfig).appId) {
      appId = (this.c as IModalExplorerConfig).appId;
    }

    return appId ?? "";
  }

  getModalTypeBasedOnBizEntityType(bizEntityTypeId: string): any {

  }

}
